import { SpanNode } from "@/types/spans";

export enum SpanStatus {
  Error = "Error",
  Running = "Running",
  Success = "Success",
  PartiallyFailed = "Partially Failed",
  Failed = "Failed",
}

export const calculateSpanStatus = (
  node: SpanNode,
  childrenStatuses: SpanStatus[] = [],
): SpanStatus => {
  const hasError =
    node.status?.status_code === "StatusCode.ERROR" ||
    node.events?.some(event => event.name === "exception");

  let status: SpanStatus;
  if (hasError) {
    status = SpanStatus.Error;
  } else if (node.end === null) {
    status = SpanStatus.Running;
  } else {
    status = SpanStatus.Success;
  }

  if (childrenStatuses.length > 0 && status !== SpanStatus.Running) {
    const hasErrorChildren = childrenStatuses.some(s => s === SpanStatus.Error);
    const hasSuccessfulChildren = childrenStatuses.some(s => s === SpanStatus.Success);
    const allChildrenError = childrenStatuses.every(
      s => s === SpanStatus.Error || s === SpanStatus.Failed,
    );
    const allChildrenSuccessful = childrenStatuses.every(s => s === SpanStatus.Success);

    if (allChildrenError) {
      status = SpanStatus.Failed;
    } else if (hasErrorChildren && hasSuccessfulChildren) {
      status = SpanStatus.PartiallyFailed;
    } else if (allChildrenSuccessful && !hasError) {
      status = SpanStatus.Success;
    } else if (childrenStatuses.some(s => s === SpanStatus.PartiallyFailed)) {
      status = SpanStatus.PartiallyFailed;
    }
  }

  return status;
};

export const calculateSpanStatuses = (spanTree: SpanNode[] | null): Map<string, SpanStatus> => {
  const statusMap = new Map<string, SpanStatus>();

  if (!spanTree) {
    return statusMap;
  }

  const processNode = (node: SpanNode): SpanStatus => {
    const childrenStatuses = node.children.map(child => processNode(child));

    const status = calculateSpanStatus(node, childrenStatuses);
    statusMap.set(node.span_id, status);

    return status;
  };

  spanTree.forEach(rootNode => {
    processNode(rootNode);
  });

  return statusMap;
};
